import { useEffect, useRef, useCallback, useState } from 'react';
import { connectOddsStream } from '../api/oddsScreenStream';

const HIGHLIGHT_DURATION_MS = 2000;
const BACKOFF_BASE_MS = 2000;
const BACKOFF_MAX_MS = 30000;

/**
 * Find the PriceEntry inside a game's selection tree that matches
 * `targetNormId` on any of the three normalised-id fields, and whose
 * `sportsbook` matches `targetBook`.
 *
 * Returns `{ entry, priceField }` where `priceField` is the key whose
 * current value equals `oldPrice`, or null if nothing matched.
 */
function findPriceEntry(selection, targetNormId, targetBook, oldPrice) {
	const PRICE_FIELDS = [
		'bet_price_over',
		'bet_price_under',
		'bet_price_over_positive',
		'bet_price_over_negative',
		'bet_price_under_positive',
		'bet_price_under_negative',
	];

	for (const outcomes of Object.values(selection)) {
		for (const entries of Object.values(outcomes)) {
			if (!Array.isArray(entries)) continue;
			for (const entry of entries) {
				if (!entry) continue;
				if (entry.sportsbook !== targetBook) continue;

				const matchesId =
					entry.outcome_id_normalized === targetNormId ||
					entry.outcome_id_normalized_over === targetNormId ||
					entry.outcome_id_normalized_under === targetNormId;

				if (!matchesId) continue;

				const oldNum = Number(oldPrice);
				for (const field of PRICE_FIELDS) {
					if (entry[field] != null && Number(entry[field]) === oldNum) {
						return { entry, priceField: field };
					}
				}

				return { entry, priceField: null };
			}
		}
	}
	return null;
}

function deepCloneGame(game) {
	const clone = { ...game };
	if (game.selection) {
		clone.selection = {};
		for (const [selName, outcomes] of Object.entries(game.selection)) {
			clone.selection[selName] = {};
			for (const [outcomeId, arr] of Object.entries(outcomes)) {
				clone.selection[selName][outcomeId] = Array.isArray(arr)
					? arr.map((e) => (e ? { ...e } : e))
					: arr;
			}
		}
	}
	return clone;
}

function applyChanges(prevData, changes) {
	if (!changes?.length) return prevData;

	const gameIndex = new Map();
	for (let i = 0; i < prevData.length; i++) {
		const g = prevData[i];
		const id = g.game_id || g.game?.game_id || g.game?.id;
		if (id) gameIndex.set(id, i);
	}

	const cloned = new Map();
	const next = [...prevData];

	for (const change of changes) {
		const { game_id, outcome_id_normalized, sportsbook, old_price, new_price } = change;
		const idx = gameIndex.get(game_id);
		if (idx == null) continue;

		if (!cloned.has(idx)) {
			next[idx] = deepCloneGame(next[idx]);
			cloned.set(idx, true);
		}

		const game = next[idx];
		if (!game.selection) continue;

		const match = findPriceEntry(game.selection, outcome_id_normalized, sportsbook, old_price);
		if (!match) continue;

		const { entry, priceField } = match;
		if (priceField) {
			entry[priceField] = new_price;
		}
	}

	return next;
}

/**
 * Hook that manages the SSE odds stream lifecycle, applies real-time
 * diffs to oddsData, and tracks highlight state with auto-clear.
 *
 * @param {Object} opts
 * @param {string}  opts.league
 * @param {string}  opts.market
 * @param {boolean} opts.isLive
 * @param {string}  opts.accessToken
 * @param {Array}   opts.oddsData       — current screener data
 * @param {Function} opts.setOddsData   — state setter for screener data
 *
 * @returns {{ highlightedPrices: Map<string, 'up'|'down'> }}
 */
export function useOddsStream({
	league,
	market,
	isLive,
	accessToken,
	oddsData,
	setOddsData,
} = {}) {
	const [highlightedPrices, setHighlightedPrices] = useState(() => new Map());

	const highlightTimers = useRef(new Map());
	const retriesRef = useRef(0);
	const disconnectRef = useRef(null);
	const oddsDataRef = useRef(oddsData);

	useEffect(() => {
		oddsDataRef.current = oddsData;
	}, [oddsData]);

	const clearAllTimers = useCallback(() => {
		for (const timer of highlightTimers.current.values()) {
			clearTimeout(timer);
		}
		highlightTimers.current.clear();
	}, []);

	const addHighlight = useCallback((outcomeNormId, direction) => {
		setHighlightedPrices((prev) => {
			const next = new Map(prev);
			next.set(outcomeNormId, direction);
			return next;
		});

		const existing = highlightTimers.current.get(outcomeNormId);
		if (existing) clearTimeout(existing);

		const timer = setTimeout(() => {
			highlightTimers.current.delete(outcomeNormId);
			setHighlightedPrices((prev) => {
				const next = new Map(prev);
				next.delete(outcomeNormId);
				return next;
			});
		}, HIGHLIGHT_DURATION_MS);

		highlightTimers.current.set(outcomeNormId, timer);
	}, []);

	const handleUpdate = useCallback(
		(data) => {
			const { changes, added, removed } = data || {};

			if (changes?.length) {
				setOddsData((prev) => applyChanges(prev || [], changes));

				for (const change of changes) {
					const dir =
						Number(change.new_price) > Number(change.old_price) ? 'up' : 'down';
					addHighlight(change.outcome_id_normalized, dir);
				}
			}

			if (added?.length) {
				setOddsData((prev) => applyChanges(prev || [], added));

				for (const entry of added) {
					if (entry.outcome_id_normalized) {
						addHighlight(entry.outcome_id_normalized, 'up');
					}
				}
			}

			if (removed?.length) {
				setOddsData((prev) => {
					if (!prev) return prev;
					const removedIds = new Set(removed.map((r) => r.outcome_id_normalized));
					return prev.map((game) => {
						if (!game.selection) return game;
						let touched = false;
						const newSel = {};
						for (const [selName, outcomes] of Object.entries(game.selection)) {
							newSel[selName] = {};
							for (const [outcomeId, arr] of Object.entries(outcomes)) {
								if (!Array.isArray(arr)) {
									newSel[selName][outcomeId] = arr;
									continue;
								}
								const filtered = arr.filter((e) => {
									if (!e) return true;
									return (
										!removedIds.has(e.outcome_id_normalized) &&
										!removedIds.has(e.outcome_id_normalized_over) &&
										!removedIds.has(e.outcome_id_normalized_under)
									);
								});
								if (filtered.length !== arr.length) touched = true;
								newSel[selName][outcomeId] = filtered;
							}
						}
						return touched ? { ...game, selection: newSel } : game;
					});
				});
			}
		},
		[setOddsData, addHighlight],
	);

	useEffect(() => {
		if (!oddsData || !oddsData.length) return;

		retriesRef.current = 0;

		function connect() {
			disconnectRef.current?.();

			disconnectRef.current = connectOddsStream({
				league,
				market,
				isLive,
				accessToken,
				onConnected() {
					retriesRef.current = 0;
				},
				onUpdate: handleUpdate,
				onError() {
					const delay = Math.min(
						BACKOFF_BASE_MS * Math.pow(2, retriesRef.current),
						BACKOFF_MAX_MS,
					);
					retriesRef.current += 1;
					setTimeout(connect, delay);
				},
			});
		}

		connect();

		return () => {
			disconnectRef.current?.();
			disconnectRef.current = null;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [league, market, isLive, accessToken, handleUpdate, Boolean(oddsData?.length)]);

	useEffect(() => {
		return () => clearAllTimers();
	}, [clearAllTimers]);

	return { highlightedPrices };
}
